// Viewport-aware placement for position:fixed popovers anchored to a trigger
// (merge-field pickers, etc.): open below the trigger when there is room,
// flip above when the trigger sits near the viewport bottom, and clamp
// max-height (with internal scrolling) when neither side fully fits.
//
// The React bundle has a mirrored copy of this math in
// app/frontend/react/lib/menuPlacement.ts (separate build pipelines — keep
// the two in sync if the placement rules ever change). A parity Jest test
// (test/javascript/lib/anchored_menu_position_parity.test.js) pins the two
// implementations to identical outputs.

export function anchoredFixedPosition(anchorRect, menuHeight, viewportHeight, gap = 6) {
  const spaceBelow = viewportHeight - anchorRect.bottom - gap
  const spaceAbove = anchorRect.top - gap
  const openUp = menuHeight > spaceBelow && spaceBelow < spaceAbove
  const space = openUp ? spaceAbove : spaceBelow
  return {
    top: openUp ? null : anchorRect.bottom + gap,
    bottom: openUp ? viewportHeight - anchorRect.top + gap : null,
    maxHeight: menuHeight > space ? space : null,
  }
}

// The left edge (viewport px) of a menu `menuWidth` wide, anchored to a
// trigger, kept `gap` inside `bounds` ({ left, right }, viewport px) — owner
// ruling 47, 2026-11-01: left-aligned at the trigger when that fits, else its
// right edge at the trigger's right edge when that fits, else clamped inside.
// Mirrors alignedMenuLeft in app/frontend/react/lib/menuPlacement.ts (pinned by
// test/javascript/lib/anchored_menu_position_parity.test.js).
export function alignedMenuLeft(anchorRect, menuWidth, bounds, gap = 4) {
  const min = bounds.left + gap
  const max = bounds.right - gap - menuWidth
  const fits = (left) => left >= min && left <= max
  if (fits(anchorRect.left)) return anchorRect.left
  const rightAligned = anchorRect.right - menuWidth
  if (fits(rightAligned)) return rightAligned
  return Math.max(min, Math.min(anchorRect.left, max))
}

// Fixed top/bottom resolve against the layout viewport (clientHeight, which
// excludes a classic horizontal scrollbar); innerHeight would misplace
// flipped menus by the scrollbar height. jsdom has no layout (clientHeight
// 0), so fall back to innerHeight there.
function viewportHeight() {
  return document.documentElement.clientHeight || window.innerHeight
}

function viewportWidth() {
  return document.documentElement.clientWidth || window.innerWidth
}

// Natural rendered height: offsetHeight respects the menu's own CSS
// max-height design cap (scrollHeight would NOT — it returns the full
// content height, flipping menus that actually fit below). Any inline
// max-height clamp from a previous open is cleared first so a clamped menu
// doesn't re-measure as "fits"; the final styles below always rewrite it.
function measureNaturalHeight(menu) {
  menu.style.maxHeight = ''
  return menu.offsetHeight
}

// Positions an in-DOM menu element against the trigger's viewport rect.
// Callers must ensure the menu is renderable (not `hidden`) before calling —
// or pass `measureHidden: true` to briefly unhide an `[hidden]` menu
// invisibly for measurement (no paint happens mid-handler, so no flicker).
// `align: 'right'` anchors the menu's right edge to the trigger's right edge
// (for triggers near the right viewport edge) instead of left-to-left.
export function positionFixedMenu(menu, anchorRect, { gap = 6, measureHidden = false, align = 'left' } = {}) {
  let restoreHidden = false
  let prevVisibility = ''
  if (measureHidden && menu.hidden) {
    prevVisibility = menu.style.visibility
    menu.style.visibility = 'hidden'
    menu.hidden = false
    restoreHidden = true
  }

  const menuHeight = measureNaturalHeight(menu)
  const pos = anchoredFixedPosition(anchorRect, menuHeight, viewportHeight(), gap)

  if (restoreHidden) {
    menu.hidden = true
    menu.style.visibility = prevVisibility
  }

  menu.style.position = 'fixed'
  if (align === 'right') {
    menu.style.right = `${viewportWidth() - anchorRect.right}px`
    menu.style.left = 'auto'
  } else {
    menu.style.left = `${anchorRect.left}px`
    menu.style.right = 'auto'
  }
  menu.style.top = pos.top != null ? `${pos.top}px` : 'auto'
  menu.style.bottom = pos.bottom != null ? `${pos.bottom}px` : 'auto'
  menu.style.maxHeight = pos.maxHeight != null ? `${pos.maxHeight}px` : ''
  menu.style.overflowY = pos.maxHeight != null ? 'auto' : ''
  shiftIntoContainingBlock(menu)
}

// position:fixed resolves against the viewport only while no ancestor
// establishes a containing block for it. An open gl-modal's .modal-dialog
// keeps `transform: scale(1)` (components/_modals.scss), which does — so a
// menu inside a modal (the Talk Track modal's merge-field picker) would land
// offset by the dialog's own position. The coordinates above are viewport
// coordinates; re-express them against that ancestor's padding box. Assumes
// an unscaled ancestor (scale(1) / translate), which is every open modal.
const FIXED_CONTAINER_PROPERTIES = ['transform', 'perspective', 'filter', 'backdropFilter']

function establishesFixedContainingBlock(style) {
  if (FIXED_CONTAINER_PROPERTIES.some((prop) => style[prop] && style[prop] !== 'none')) return true
  if (/paint|layout|strict|content/.test(style.contain || '')) return true
  return /transform|perspective|filter/.test(style.willChange || '')
}

export function fixedContainingBlock(element) {
  for (let node = element.parentElement; node && node !== document.documentElement; node = node.parentElement) {
    if (establishesFixedContainingBlock(window.getComputedStyle(node))) return node
  }
  return null
}

function shiftIntoContainingBlock(menu) {
  const container = fixedContainingBlock(menu)
  if (!container) return

  const box = container.getBoundingClientRect()
  const left = box.left + container.clientLeft
  const top = box.top + container.clientTop
  const right = viewportWidth() - (left + container.clientWidth)
  const bottom = viewportHeight() - (top + container.clientHeight)
  const shift = (prop, by) => {
    const value = parseFloat(menu.style[prop])
    if (!Number.isNaN(value)) menu.style[prop] = `${value - by}px`
  }
  shift('left', left)
  shift('top', top)
  shift('right', right)
  shift('bottom', bottom)
}
